import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { Contract } from '../types/contract'
import { centsClient } from '../api/cents/clients/cents-client'
import { Transaction } from '../types/donation/transaction'

interface ContractContextType {
  contract: Contract | undefined
  transactions: Transaction[] | undefined
  error: 'contract_retrieve' | 'transactions_retrieve' | 'contract_save' | undefined
  unsavedChanges: Partial<Contract> | undefined
  state: 'idle' | 'retrieving' | 'saving'
  update: (value: Partial<Contract>) => void
  clearUpdates: () => void
  saveUpdates: () => Promise<void>
  patch: (value: Partial<Contract>) => Promise<void>
  retrieve: () => Promise<void>
  retrieveContract: () => Promise<Contract | undefined>
  retrieveTransactions: () => Promise<void>
}

const ContractContext = createContext<ContractContextType | undefined>(undefined)

export const ContractProvider: React.FC<{ charityId: string; id: string; children: React.ReactNode }> = ({ charityId, id, children }) => {
  const [contract, setContract] = useState<Contract>()
  const [transactions, setTransactions] = useState<Transaction[]>()
  const [updatingContract, setUpdatingContract] = useState<Contract>()
  const [contractUnsavedUpdate, setContractUnsavedUpdate] = useState<Partial<Contract>>()
  const [error, setError] = useState<'contract_retrieve' | 'transactions_retrieve' | 'contract_save'>()
  const [state, setState] = useState<'idle' | 'retrieving' | 'saving'>('idle')

  const retrieveTransactions = useCallback(
    async (contractToUse = contract) => {
      if (!contractToUse?.charityId) {
        throw new Error('Charity not found')
      }
      if (!contractToUse?._id) {
        throw new Error('Contract not found')
      }
      try {
        const transactions = await centsClient.contracts.getTransactions(contractToUse.charityId, contractToUse._id)
        setTransactions(transactions)
      } catch {
        setError('transactions_retrieve')
      }
    },
    [contract]
  )

  const retrieveContract = useCallback(async () => {
    try {
      setState('retrieving')
      const res = await centsClient.contracts.getContractsById(charityId, id)
      setContract(res)
      return res
    } catch {
      setError('contract_retrieve')
    } finally {
      setState('idle')
    }
  }, [charityId, id])

  const retrieve = useCallback(async () => {
    void retrieveContract().then(contract => {
      void retrieveTransactions(contract)
    })
  }, [retrieveContract, retrieveTransactions])

  const update = useCallback(
    (value: Partial<Contract>) => {
      setContractUnsavedUpdate(prev => {
        const result = { ...prev }
        applyUpdates(contract, result, value)
        return Object.keys(result).length === 0 ? undefined : result
      })
      setUpdatingContract(prev => {
        return { ...prev, ...value }
      })
    },
    [setContractUnsavedUpdate, setUpdatingContract, contract]
  )

  const clearUpdates = useCallback(() => {
    setUpdatingContract(contract)
    setContractUnsavedUpdate(undefined)
  }, [contract])

  const patch = useCallback(
    async (value: Partial<Contract>) => {
      if (!contract?._id) throw new Error('Contract not set')
      if (!contract?.charityId) {
        throw new Error('Charity not found')
      }
      // Parse empty strings or undefineds as null
      Object.keys(value).forEach(k => {
        if ((typeof value[k] === 'string' && (value[k] as string).trim() === '') || value[k] === undefined) {
          value[k] = null
        }
      })

      const res = await centsClient.contracts.patchContract(contract.charityId, contract._id, value)
      setContract(res)
    },
    [contract]
  )

  const saveUpdates = useCallback(async () => {
    try {
      setState('saving')
      if (contractUnsavedUpdate) {
        await patch(contractUnsavedUpdate)
        setContractUnsavedUpdate(undefined)
      }
    } catch {
      setError('contract_save')
    } finally {
      setState('idle')
    }
  }, [contractUnsavedUpdate, setContractUnsavedUpdate, patch])

  useEffect(() => {
    setUpdatingContract({ ...contract, ...contractUnsavedUpdate })
  }, [contract])

  return (
    <ContractContext.Provider
      value={{
        contract: updatingContract,
        transactions,
        error,
        unsavedChanges: contractUnsavedUpdate,
        state,
        update,
        clearUpdates,
        saveUpdates: saveUpdates,
        patch: patch,
        retrieve,
        retrieveContract,
        retrieveTransactions,
      }}
    >
      {children}
    </ContractContext.Provider>
  )
}

export const useContract = () => {
  const context = useContext(ContractContext)
  if (!context) {
    throw new Error('useContract must be used within a ContractProvider')
  }
  return context
}

/**
 * Apply updates to the current contract, deleting keys if the value is the same as the current one
 * @param contract Current contract value
 * @param currentUpdates Current updates
 * @param updates Updates to be applied
 */
function applyUpdates(contract: Contract | undefined, currentUpdates: Partial<Contract>, updates: Partial<Contract>): void {
  Object.keys(updates).forEach(key => {
    if (
      contract?.[key] === updates[key] ||
      ((contract?.[key] === undefined || contract?.[key] === '' || contract?.[key] === null) &&
        (updates[key] === '' || updates[key] === undefined || updates[key] === null))
    ) {
      delete currentUpdates[key]
    } else {
      currentUpdates[key] = updates[key]
    }
  })
}
